import * as THREE from 'three';

/**
 * Procedural High-Fidelity 3D Boy & Girl Romantic Avatars for Valentine Experience
 * Built with organic geometric primitives, soft matte tactile shaders, and expressive emotional joints.
 */
export class RomanticCoupleAvatars {
  constructor() {
    this.root = new THREE.Group();
    this.root.name = 'RomanticCouple';

    // Materials Palette (Warm, Tactile, Pastel Luxury)
    this.materials = {
      skin: new THREE.MeshStandardMaterial({
        color: 0xFFF3EB,
        roughness: 0.65,
        metalness: 0.05
      }),
      boyHair: new THREE.MeshStandardMaterial({
        color: 0x3D2E28,
        roughness: 0.7,
        metalness: 0.1
      }),
      girlHair: new THREE.MeshStandardMaterial({
        color: 0x5C382A,
        roughness: 0.65,
        metalness: 0.1
      }),
      boySweater: new THREE.MeshStandardMaterial({
        color: 0xE8DFD5, // Warm Oat Cashmere
        roughness: 0.85,
        metalness: 0.0
      }),
      boyPants: new THREE.MeshStandardMaterial({
        color: 0x383432, // Charcoal Tailored Trousers
        roughness: 0.8,
        metalness: 0.1
      }),
      girlDress: new THREE.MeshStandardMaterial({
        color: 0xEAAFA9, // Soft Dusty Rose Blush
        roughness: 0.75,
        metalness: 0.05
      }),
      girlRibbon: new THREE.MeshStandardMaterial({
        color: 0xC95D63, // Deep Rose Ribbon
        roughness: 0.4,
        metalness: 0.2
      }),
      blush: new THREE.MeshBasicMaterial({
        color: 0xFF9E9E,
        transparent: true,
        opacity: 0.65
      }),
      eye: new THREE.MeshBasicMaterial({
        color: 0x1E1819
      }),
      eyeSparkle: new THREE.MeshBasicMaterial({
        color: 0xFFFFFF
      }),
      roseRed: new THREE.MeshStandardMaterial({
        color: 0xC7253E,
        roughness: 0.4,
        metalness: 0.2
      }),
      goldAccent: new THREE.MeshStandardMaterial({
        color: 0xE5B869,
        roughness: 0.25,
        metalness: 0.7
      })
    };

    this.boy = this.createBoy();
    this.girl = this.createGirl();
    this.sharedHeart = this.createFloatingHeart();

    this.boy.position.set(-0.7, 0, 0);
    this.girl.position.set(0.7, 0, 0);

    this.root.add(this.boy);
    this.root.add(this.girl);
    this.root.add(this.sharedHeart);

    // Initial hidden state beneath pedestal
    this.root.position.y = -5.0;
    this.root.scale.set(0.01, 0.01, 0.01);

    // Emotion state tracker
    this.emotion = 'shy'; // 'shy', 'gazing', 'happy', 'proposal', 'celebrate'
    this.animTime = 0;
  }

  createBoy() {
    const boy = new THREE.Group();
    boy.name = 'Boy';

    // 1. Body (Cashmere Sweater)
    const bodyGeo = new THREE.CylinderGeometry(0.38, 0.44, 0.9, 20);
    const body = new THREE.Mesh(bodyGeo, this.materials.boySweater);
    body.position.y = 0.85;
    body.castShadow = true;
    boy.add(body);
    boy.userData.body = body;

    // Collar
    const collarGeo = new THREE.TorusGeometry(0.24, 0.06, 12, 24);
    const collar = new THREE.Mesh(collarGeo, this.materials.boySweater);
    collar.rotation.x = Math.PI / 2;
    collar.position.y = 1.3;
    boy.add(collar);

    // 2. Head Group
    const headGroup = new THREE.Group();
    headGroup.position.y = 1.65;

    const headGeo = new THREE.SphereGeometry(0.42, 24, 20);
    headGeo.scale(1, 1.08, 1);
    const headMesh = new THREE.Mesh(headGeo, this.materials.skin);
    headMesh.castShadow = true;
    headGroup.add(headMesh);

    // Hair
    const hairGeo = new THREE.SphereGeometry(0.45, 20, 16);
    hairGeo.scale(1.02, 0.75, 1.05);
    const hair = new THREE.Mesh(hairGeo, this.materials.boyHair);
    hair.position.set(0, 0.18, -0.04);
    headGroup.add(hair);

    // Hair front bangs
    const bangGeo = new THREE.CylinderGeometry(0.1, 0.28, 0.35, 12);
    const bang = new THREE.Mesh(bangGeo, this.materials.boyHair);
    bang.rotation.z = 0.3;
    bang.position.set(-0.15, 0.28, 0.32);
    headGroup.add(bang);

    // Eyes
    const eyeGeo = new THREE.SphereGeometry(0.045, 12, 12);
    const sparkleGeo = new THREE.SphereGeometry(0.016, 8, 8);

    const leftEye = new THREE.Mesh(eyeGeo, this.materials.eye);
    leftEye.position.set(-0.14, 0.02, 0.38);
    const leftSparkle = new THREE.Mesh(sparkleGeo, this.materials.eyeSparkle);
    leftSparkle.position.set(-0.01, 0.015, 0.035);
    leftEye.add(leftSparkle);
    headGroup.add(leftEye);

    const rightEye = new THREE.Mesh(eyeGeo, this.materials.eye);
    rightEye.position.set(0.14, 0.02, 0.38);
    const rightSparkle = new THREE.Mesh(sparkleGeo, this.materials.eyeSparkle);
    rightSparkle.position.set(-0.01, 0.015, 0.035);
    rightEye.add(rightSparkle);
    headGroup.add(rightEye);

    // Blush
    const blushGeo = new THREE.SphereGeometry(0.065, 12, 8);
    blushGeo.scale(1.3, 0.6, 0.4);
    const leftBlush = new THREE.Mesh(blushGeo, this.materials.blush);
    leftBlush.position.set(-0.22, -0.09, 0.34);
    headGroup.add(leftBlush);

    const rightBlush = new THREE.Mesh(blushGeo, this.materials.blush);
    rightBlush.position.set(0.22, -0.09, 0.34);
    headGroup.add(rightBlush);

    boy.add(headGroup);
    boy.userData.head = headGroup;

    // 3. Legs
    const legGeo = new THREE.CylinderGeometry(0.12, 0.11, 0.8, 14);
    const leftLeg = new THREE.Mesh(legGeo, this.materials.boyPants);
    leftLeg.position.set(-0.18, 0.4, 0);
    leftLeg.castShadow = true;
    boy.add(leftLeg);

    const rightLeg = new THREE.Mesh(legGeo, this.materials.boyPants);
    rightLeg.position.set(0.18, 0.4, 0);
    rightLeg.castShadow = true;
    boy.add(rightLeg);

    // Shoes
    const shoeGeo = new THREE.BoxGeometry(0.14, 0.08, 0.22);
    const leftShoe = new THREE.Mesh(shoeGeo, this.materials.boyHair);
    leftShoe.position.set(-0.18, 0.04, 0.04);
    boy.add(leftShoe);

    const rightShoe = new THREE.Mesh(shoeGeo, this.materials.boyHair);
    rightShoe.position.set(0.18, 0.04, 0.04);
    boy.add(rightShoe);

    // 4. Arms with Seamless Shoulder Ball-Joints & Hands
    const armGeo = new THREE.CylinderGeometry(0.09, 0.08, 0.58, 14);
    const shoulderGeo = new THREE.SphereGeometry(0.105, 14, 12);
    const handGeo = new THREE.SphereGeometry(0.08, 12, 10);
    
    // Left Arm
    const leftArm = new THREE.Group();
    leftArm.position.set(-0.35, 1.18, 0);
    const leftShoulder = new THREE.Mesh(shoulderGeo, this.materials.boySweater);
    leftArm.add(leftShoulder);

    const leftArmMesh = new THREE.Mesh(armGeo, this.materials.boySweater);
    leftArmMesh.position.y = -0.26;
    leftArm.add(leftArmMesh);

    const leftHand = new THREE.Mesh(handGeo, this.materials.skin);
    leftHand.position.y = -0.54;
    leftArm.add(leftHand);

    boy.add(leftArm);
    boy.userData.leftArm = leftArm;

    // Right Arm
    const rightArm = new THREE.Group();
    rightArm.position.set(0.35, 1.18, 0);
    const rightShoulder = new THREE.Mesh(shoulderGeo, this.materials.boySweater);
    rightArm.add(rightShoulder);

    const rightArmMesh = new THREE.Mesh(armGeo, this.materials.boySweater);
    rightArmMesh.position.y = -0.26;
    rightArm.add(rightArmMesh);

    const rightHand = new THREE.Mesh(handGeo, this.materials.skin);
    rightHand.position.y = -0.54;
    rightArm.add(rightHand);
    
    // Detailed Realistic 3D Blooming Rose in Boy's hand
    const roseGroup = this.createRealisticRose();
    roseGroup.position.set(0, -0.45, 0.15);
    roseGroup.rotation.x = -0.3;
    rightArm.add(roseGroup);
    boy.userData.rose = roseGroup;

    boy.add(rightArm);
    boy.userData.rightArm = rightArm;

    return boy;
  }

  createGirl() {
    const girl = new THREE.Group();
    girl.name = 'Girl';

    // 1. Body & Flared Dress
    const bodyGeo = new THREE.CylinderGeometry(0.32, 0.38, 0.75, 20);
    const body = new THREE.Mesh(bodyGeo, this.materials.girlDress);
    body.position.y = 0.95;
    body.castShadow = true;
    girl.add(body);
    girl.userData.body = body;

    // Flared Skirt
    const skirtGeo = new THREE.ConeGeometry(0.55, 0.65, 22, 1, true);
    const skirt = new THREE.Mesh(skirtGeo, this.materials.girlDress);
    skirt.position.y = 0.62;
    skirt.castShadow = true;
    girl.add(skirt);

    // Belt / Ribbon
    const ribbonGeo = new THREE.TorusGeometry(0.34, 0.04, 10, 24);
    const ribbon = new THREE.Mesh(ribbonGeo, this.materials.girlRibbon);
    ribbon.rotation.x = Math.PI / 2;
    ribbon.position.y = 0.85;
    girl.add(ribbon);

    // 2. Head Group
    const headGroup = new THREE.Group();
    headGroup.position.y = 1.62;

    const headGeo = new THREE.SphereGeometry(0.4, 24, 20);
    headGeo.scale(1, 1.05, 1);
    const headMesh = new THREE.Mesh(headGeo, this.materials.skin);
    headMesh.castShadow = true;
    headGroup.add(headMesh);

    // Hair base
    const hairGeo = new THREE.SphereGeometry(0.44, 20, 16);
    hairGeo.scale(1.04, 0.85, 1.06);
    const hair = new THREE.Mesh(hairGeo, this.materials.girlHair);
    hair.position.set(0, 0.15, -0.06);
    headGroup.add(hair);

    // Cute Hair Bun with ribbon
    const bunGeo = new THREE.SphereGeometry(0.2, 16, 14);
    const bun = new THREE.Mesh(bunGeo, this.materials.girlHair);
    bun.position.set(0, 0.48, -0.22);
    headGroup.add(bun);

    const bunRibbonGeo = new THREE.TorusGeometry(0.12, 0.03, 8, 16);
    const bunRibbon = new THREE.Mesh(bunRibbonGeo, this.materials.girlRibbon);
    bunRibbon.position.set(0, 0.42, -0.18);
    bunRibbon.rotation.x = 0.4;
    headGroup.add(bunRibbon);

    // Side hair curls
    const curlGeo = new THREE.CylinderGeometry(0.06, 0.04, 0.5, 10);
    const leftCurl = new THREE.Mesh(curlGeo, this.materials.girlHair);
    leftCurl.position.set(-0.38, -0.1, 0.1);
    leftCurl.rotation.z = 0.2;
    headGroup.add(leftCurl);

    const rightCurl = new THREE.Mesh(curlGeo, this.materials.girlHair);
    rightCurl.position.set(0.38, -0.1, 0.1);
    rightCurl.rotation.z = -0.2;
    headGroup.add(rightCurl);

    // Eyes
    const eyeGeo = new THREE.SphereGeometry(0.042, 12, 12);
    const sparkleGeo = new THREE.SphereGeometry(0.015, 8, 8);

    const leftEye = new THREE.Mesh(eyeGeo, this.materials.eye);
    leftEye.position.set(-0.13, 0.01, 0.36);
    const leftSparkle = new THREE.Mesh(sparkleGeo, this.materials.eyeSparkle);
    leftSparkle.position.set(-0.01, 0.012, 0.032);
    leftEye.add(leftSparkle);
    headGroup.add(leftEye);

    const rightEye = new THREE.Mesh(eyeGeo, this.materials.eye);
    rightEye.position.set(0.13, 0.01, 0.36);
    const rightSparkle = new THREE.Mesh(sparkleGeo, this.materials.eyeSparkle);
    rightSparkle.position.set(-0.01, 0.012, 0.032);
    rightEye.add(rightSparkle);
    headGroup.add(rightEye);

    // Soft Blush
    const blushGeo = new THREE.SphereGeometry(0.07, 12, 8);
    blushGeo.scale(1.4, 0.7, 0.4);
    const leftBlush = new THREE.Mesh(blushGeo, this.materials.blush);
    leftBlush.position.set(-0.21, -0.09, 0.33);
    headGroup.add(leftBlush);

    const rightBlush = new THREE.Mesh(blushGeo, this.materials.blush);
    rightBlush.position.set(0.21, -0.09, 0.33);
    headGroup.add(rightBlush);

    girl.add(headGroup);
    girl.userData.head = headGroup;

    // 3. Legs
    const legGeo = new THREE.CylinderGeometry(0.09, 0.08, 0.5, 12);
    const leftLeg = new THREE.Mesh(legGeo, this.materials.skin);
    leftLeg.position.set(-0.14, 0.25, 0);
    girl.add(leftLeg);

    const rightLeg = new THREE.Mesh(legGeo, this.materials.skin);
    rightLeg.position.set(0.14, 0.25, 0);
    girl.add(rightLeg);

    // Cute Shoes
    const shoeGeo = new THREE.BoxGeometry(0.12, 0.07, 0.18);
    const leftShoe = new THREE.Mesh(shoeGeo, this.materials.girlRibbon);
    leftShoe.position.set(-0.14, 0.03, 0.02);
    girl.add(leftShoe);

    const rightShoe = new THREE.Mesh(shoeGeo, this.materials.girlRibbon);
    rightShoe.position.set(0.14, 0.03, 0.02);
    girl.add(rightShoe);

    // 4. Arms with Seamless Shoulder Ball-Joints & Hands
    const armGeo = new THREE.CylinderGeometry(0.08, 0.07, 0.54, 14);
    const girlShoulderGeo = new THREE.SphereGeometry(0.095, 14, 12);
    const girlHandGeo = new THREE.SphereGeometry(0.075, 12, 10);
    
    // Left Arm
    const leftArm = new THREE.Group();
    leftArm.position.set(-0.31, 1.15, 0);
    const leftShoulder = new THREE.Mesh(girlShoulderGeo, this.materials.skin);
    leftArm.add(leftShoulder);

    const leftArmMesh = new THREE.Mesh(armGeo, this.materials.skin);
    leftArmMesh.position.y = -0.24;
    leftArm.add(leftArmMesh);

    const leftHand = new THREE.Mesh(girlHandGeo, this.materials.skin);
    leftHand.position.y = -0.5;
    leftArm.add(leftHand);

    girl.add(leftArm);
    girl.userData.leftArm = leftArm;

    // Right Arm
    const rightArm = new THREE.Group();
    rightArm.position.set(0.31, 1.15, 0);
    const rightShoulder = new THREE.Mesh(girlShoulderGeo, this.materials.skin);
    rightArm.add(rightShoulder);

    const rightArmMesh = new THREE.Mesh(armGeo, this.materials.skin);
    rightArmMesh.position.y = -0.24;
    rightArm.add(rightArmMesh);

    const rightHand = new THREE.Mesh(girlHandGeo, this.materials.skin);
    rightHand.position.y = -0.5;
    rightArm.add(rightHand);

    girl.add(rightArm);
    girl.userData.rightArm = rightArm;
    return girl;
  }

  createRealisticRose() {
    const rose = new THREE.Group();
    rose.name = 'RealisticRose3D';

    // 1. Stem (Long graceful curved green stem)
    const stemGeo = new THREE.CylinderGeometry(0.018, 0.014, 0.48, 12);
    const greenMat = new THREE.MeshStandardMaterial({
      color: 0x2A623D,
      roughness: 0.6,
      metalness: 0.1
    });
    const stem = new THREE.Mesh(stemGeo, greenMat);
    stem.position.y = 0.2;
    stem.rotation.z = -0.05;
    rose.add(stem);

    // 2. Leaves attached to stem
    const leafGeo = new THREE.ConeGeometry(0.06, 0.16, 6);
    leafGeo.scale(1.2, 1, 0.25);
    const leaf1 = new THREE.Mesh(leafGeo, greenMat);
    leaf1.position.set(0.06, 0.22, 0);
    leaf1.rotation.z = -Math.PI / 3;
    rose.add(leaf1);

    const leaf2 = new THREE.Mesh(leafGeo, greenMat);
    leaf2.position.set(-0.06, 0.14, 0);
    leaf2.rotation.z = Math.PI / 3;
    rose.add(leaf2);

    // 3. Green Sepals (4 base leaves beneath bloom)
    for (let i = 0; i < 4; i++) {
      const sepalGeo = new THREE.ConeGeometry(0.04, 0.12, 4);
      sepalGeo.scale(1, 1, 0.2);
      const sepal = new THREE.Mesh(sepalGeo, greenMat);
      const angle = (i * Math.PI) / 2;
      sepal.position.set(Math.cos(angle) * 0.04, 0.42, Math.sin(angle) * 0.04);
      sepal.rotation.x = Math.sin(angle) * 0.5;
      sepal.rotation.z = -Math.cos(angle) * 0.5;
      rose.add(sepal);
    }

    // 4. Blooming Red Velvet Rose Head
    const bloomGroup = new THREE.Group();
    bloomGroup.position.y = 0.48;

    const roseVelvetMat = new THREE.MeshStandardMaterial({
      color: 0xC71F37,
      roughness: 0.42,
      metalness: 0.15
    });

    const roseDarkMat = new THREE.MeshStandardMaterial({
      color: 0x930E22,
      roughness: 0.5,
      metalness: 0.1
    });

    // Inner Core Bud
    const coreGeo = new THREE.ConeGeometry(0.07, 0.14, 12);
    const core = new THREE.Mesh(coreGeo, roseDarkMat);
    core.rotation.x = Math.PI;
    bloomGroup.add(core);

    // Middle Layer Petals (5 curved overlapping petals)
    for (let i = 0; i < 5; i++) {
      const petalGeo = new THREE.SphereGeometry(0.075, 10, 8);
      petalGeo.scale(1.2, 1.4, 0.4);
      const petal = new THREE.Mesh(petalGeo, roseVelvetMat);
      const angle = (i * Math.PI * 2) / 5;
      petal.position.set(Math.cos(angle) * 0.045, -0.02, Math.sin(angle) * 0.045);
      petal.rotation.y = angle;
      petal.rotation.x = 0.35;
      bloomGroup.add(petal);
    }

    // Outer Flared Layer Petals (6 large open petals)
    for (let i = 0; i < 6; i++) {
      const outerGeo = new THREE.SphereGeometry(0.095, 12, 8);
      outerGeo.scale(1.3, 1.2, 0.35);
      const outerPetal = new THREE.Mesh(outerGeo, roseVelvetMat);
      const angle = (i * Math.PI * 2) / 6 + 0.3;
      outerPetal.position.set(Math.cos(angle) * 0.07, -0.05, Math.sin(angle) * 0.07);
      outerPetal.rotation.y = angle;
      outerPetal.rotation.x = 0.55;
      bloomGroup.add(outerPetal);
    }

    rose.add(bloomGroup);
    rose.scale.set(1.4, 1.4, 1.4);

    return rose;
  }

  createFloatingHeart() {
    const heartGroup = new THREE.Group();
    
    const x = 0, y = 0;
    const heartShape = new THREE.Shape();
    heartShape.moveTo(x + 0.25, y + 0.25);
    heartShape.bezierCurveTo(x + 0.25, y + 0.25, x + 0.2, y, x, y);
    heartShape.bezierCurveTo(x - 0.3, y, x - 0.3, y + 0.35, x - 0.3, y + 0.35);
    heartShape.bezierCurveTo(x - 0.3, y + 0.55, x - 0.1, y + 0.77, x + 0.25, y + 1.0);
    heartShape.bezierCurveTo(x + 0.6, y + 0.77, x + 0.8, y + 0.55, x + 0.8, y + 0.35);
    heartShape.bezierCurveTo(x + 0.8, y + 0.35, x + 0.8, y, x + 0.5, y);
    heartShape.bezierCurveTo(x + 0.35, y, x + 0.25, y + 0.25, x + 0.25, y + 0.25);

    const extrudeSettings = {
      depth: 0.12,
      bevelEnabled: true,
      bevelSegments: 4,
      steps: 2,
      bevelSize: 0.04,
      bevelThickness: 0.04
    };

    const heartGeo = new THREE.ExtrudeGeometry(heartShape, extrudeSettings);
    heartGeo.center();
    heartGeo.scale(0.4, 0.4, 0.4);

    const heartMat = new THREE.MeshStandardMaterial({
      color: 0xFF4D6D,
      roughness: 0.3,
      metalness: 0.2,
      emissive: 0x8E1616,
      emissiveIntensity: 0.4
    });

    const heartMesh = new THREE.Mesh(heartGeo, heartMat);
    heartMesh.rotation.z = Math.PI;
    heartGroup.add(heartMesh);

    heartGroup.position.set(0, 2.3, 0.2);
    heartGroup.scale.set(0, 0, 0); // starts scale 0 until revealed

    return heartGroup;
  }

  /**
   * Continuous Tick Animation with Emotional Physics
   */
  update(delta, scrollProgress = 0, mouseX = 0, mouseY = 0) {
    this.animTime += delta;
    const t = this.animTime;

    // Organic Breathing & Idle Sway
    const breathe = Math.sin(t * 2.2) * 0.02;
    const boySway = Math.sin(t * 1.5) * 0.03;
    const girlSway = Math.cos(t * 1.5) * 0.03;

    if (this.boy.userData.body) {
      this.boy.userData.body.scale.set(1 + breathe * 0.5, 1 + breathe, 1 + breathe * 0.5);
    }
    if (this.girl.userData.body) {
      this.girl.userData.body.scale.set(1 + breathe * 0.5, 1 + breathe, 1 + breathe * 0.5);
    }

    // Dynamic Head Orientation (Looking at each other + subtle mouse influence)
    if (this.boy.userData.head) {
      this.boy.userData.head.rotation.y = 0.35 + mouseX * 0.15 + boySway;
      this.boy.userData.head.rotation.x = -0.05 + mouseY * 0.1;
      this.boy.userData.head.rotation.z = -0.08 + Math.sin(t * 2) * 0.02;
    }
    if (this.girl.userData.head) {
      this.girl.userData.head.rotation.y = -0.38 + mouseX * 0.15 + girlSway;
      this.girl.userData.head.rotation.x = -0.05 + mouseY * 0.1;
      this.girl.userData.head.rotation.z = 0.09 + Math.cos(t * 2) * 0.02;
    }

    // Arm gestures
    if (this.boy.userData.rightArm) {
      // Reaching gently to girl with the rose
      this.boy.userData.rightArm.rotation.z = -0.45 + Math.sin(t * 2) * 0.05;
      this.boy.userData.rightArm.rotation.x = 0.35;
    }
    if (this.girl.userData.leftArm) {
      // Reaching gently to boy
      this.girl.userData.leftArm.rotation.z = 0.48 + Math.cos(t * 2) * 0.05;
      this.girl.userData.leftArm.rotation.x = 0.3;
    }

    // Floating Shared Heart Pulse
    if (this.sharedHeart) {
      const heartBeat = 1 + Math.sin(t * 5.0) * 0.12;
      this.sharedHeart.position.y = 2.35 + Math.sin(t * 2.5) * 0.08;
      this.sharedHeart.rotation.y = Math.sin(t * 1.8) * 0.25;
      if (this.root.position.y > -1.0) {
        this.sharedHeart.scale.set(heartBeat, heartBeat, heartBeat);
      }
    }
  }

  setEmotion(type) {
    this.emotion = type;
    if (type === 'celebrate') {
      if (this.boy.userData.leftArm) this.boy.userData.leftArm.rotation.z = 1.2;
      if (this.girl.userData.rightArm) this.girl.userData.rightArm.rotation.z = -1.2;
    }
  }
}
